import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '~/services/api';
import useStore, { storeState } from '~/store';
import { isArideParkFlowFromAPI } from '~/utils/promotionUtils';
import { reportError } from '~/utils/reportError';
import { getEquipmentIdFromType, searchEquipmentNameById } from '~/utils/searchNameEquipment';

export interface UseQuoteDataOptions {
  redirectToHome?: boolean;
  quoteParamName?: string;
  /** Storybook: não chama a API; preencher a store com fixtures na story */
  skipFetch?: boolean;
}

export interface QuoteApiResponse {
  quote_id?: number;
  quote_hash: string;
  plan_id: number;
  quote_date: string;
  quote_expiry_date: string;
  installments: number;
  financial_charges: number;
  plan_has_club: boolean;
  plan_club_name: string;
  plan_club_description: string;
  flat_rate: number;
  subtotal: number;
  total: number;
  insured_item: Array<{
    insured_equipament_id: number;
    equipment_type: string;
    manufacturer: string;
    year_of_manufacture: number;
    model: string;
    serial_number: string;
    equipment_value: number;
    value_of_accessories: number;
    has_invoice: boolean;
    is_new: boolean;
  }>;
  quotes: Array<{
    product_id: number;
    product_name: string;
    product_description: string;
    product_price: number;
    checked: boolean;
    required: boolean;
    cover: Array<{
      cover_name: string;
      cover_description: string;
      cover_icon: string;
    }>;
  }>;
  upsell?: Array<{
    product_id: number;
    product_name: string;
    product_description: string;
    product_price: number;
    is_checked?: boolean;
    cover: Array<{
      cover_name: string;
      cover_description: string;
      cover_icon: string;
    }>;
  }>;
  membership_fee: number;
  is_control_membership_fee_payment_system: boolean;
  plan_type?: number;
  type_club: number;
  additionalInfo: {
    membershipFee_payment_status: number;
    has_contract: boolean;
  };
  insured: {
    id: string;
    name: string;
    document: string;
  };
  promotional_months: number;
  customer?: {
    name: string;
    email: string;
    mobile_phone: string;
    document: string;
    document_type: string;
    birthdate: string;
  };
  payment?: {
    membership_fee?: {
      status: number;
    };
  };
}

/**
 * Hook para gerenciar dados de cotação com otimizações para prevenir loops
 *
 * Otimizações implementadas:
 * - useMemo: Memoiza parâmetros da URL para evitar re-renders desnecessários
 * - useCallback: Memoiza funções para evitar re-criação em cada render
 * - Dependências controladas: useEffect com dependências específicas e memoizadas
 * - Separação de responsabilidades: Effects separados para fetch e redirecionamento
 */
export const useQuoteData = (options: UseQuoteDataOptions = {}) => {
  const { redirectToHome = true, quoteParamName = 'quote', skipFetch = false } = options;

  const [quoteLoading, setQuoteLoading] = useState(true);
  const [quoteData, setQuoteData] = useState<QuoteApiResponse | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Memoizar parâmetros da URL para evitar re-renders desnecessários
  const urlParams = useMemo(
    () => ({
      plan: searchParams.get('plan'),
      distribuitor: searchParams.get('dis'),
      org: searchParams.get('org'),
      seller: searchParams.get('seller'),
      quoteId: searchParams.get(quoteParamName)
    }),
    [searchParams, quoteParamName]
  );

  const { quotes } = useStore((store) => ({
    quotes: store.quotes
  }));

  // Memoizar função de fetch com useCallback para evitar re-criação
  const fetchQuoteData = useCallback(async () => {
    if (skipFetch) {
      setQuoteLoading(false);
      return;
    }
    console.log('🚀 useQuoteData: fetchQuoteData iniciado', urlParams);
    setQuoteLoading(true);

    if (!urlParams.quoteId) {
      console.warn('❌ Quote ID não encontrado nos parâmetros da URL');
      setQuoteLoading(false);
      return;
    }

    try {
      const queryParams = urlParams.org ? { organization_id: urlParams.org } : {};
      console.log('📡 Fazendo GET /Quotes/', urlParams.quoteId, queryParams);
      const response = await api.v2!.get(`/Quotes/${urlParams.quoteId}`, { params: queryParams });
      const data: QuoteApiResponse = response.data.data;
      console.log('✅ Resposta da API recebida:', data);

      setQuoteData(data);

      // Persistir organization_id da URL na store para uso nas rotas de cotação
      if (urlParams.org) {
        useStore.getState().setOrganizationId(urlParams.org);
      }

      // Atualizar store com os dados da cotação
      useStore.setState((state) => ({
        planOptions: {
          ...state.planOptions,
          payment: {
            ...state.planOptions.payment,
            membership_fee: {
              ...state.planOptions.payment.membership_fee,
              methods: data.is_control_membership_fee_payment_system ? ['pix'] : []
            },
            installments: {
              ...state.planOptions.payment.installments,
              methods: ['credit_card']
            }
          }
        }
      }));

      // Atualizar equipment e insured_item se disponível
      if (data.insured_item && Array.isArray(data.insured_item) && data.insured_item.length > 0) {
        const firstItem = data.insured_item[0];

        // Mapear equipment_type para ID e depois para o valor do equipment
        const equipmentTypeId = getEquipmentIdFromType(firstItem.equipment_type);
        const mappedEquipment = searchEquipmentNameById(equipmentTypeId);

        // Usar os métodos específicos do store para atualizar os dados
        const store = useStore.getState();
        store.setEquipment(mappedEquipment);
        store.setEquipmentValue(firstItem.equipment_value);
        store.setYearOfManufacture(firstItem.year_of_manufacture);
        store.setAccessoriesValue(firstItem.value_of_accessories);
        store.setHasInvoice(firstItem.has_invoice);
        store.setIsNewEquipment(firstItem.is_new);

        // Atualizar insured_item com o equipment_type_id mapeado corretamente
        const insuredItemWithTypeId = data.insured_item.map((item) => ({
          ...item,
          equipment_type_id: getEquipmentIdFromType(item.equipment_type)
        }));

        useStore.setState({
          insured_item: insuredItemWithTypeId as any
        });
      }

      // Atualizar dados do segurado se disponíveis
      if (data.insured) {
        useStore.setState({
          insured: data.insured
        } as any);
      }

      // Atualizar quoteResponse (API v2 pode não retornar quote_id; usar quote_hash como fallback)
      const quoteIdStr = data.quote_id != null ? String(data.quote_id) : data.quote_hash ?? '';
      const totalNum = Number(data.total) || 0;
      const financialChargesNum = Number(data.financial_charges) || 0;
      const membershipFeeNum = Number(data.membership_fee) || 0;

      useStore.setState((state) => ({
        quoteResponse: {
          ...state.quoteResponse,
          quote_id: quoteIdStr,
          quote_hash: data.quote_hash ?? '',
          plan_id: data.plan_id ?? 0,
          quote_date: data.quote_date ?? '',
          quote_expiry_date: data.quote_expiry_date ?? '',
          installments: data.installments ?? 0,
          financial_charges: financialChargesNum,
          total: totalNum,
          subtotal: Number(data.subtotal) || 0,
          plan_has_club: data.plan_has_club ?? false,
          plan_club_name: data.plan_club_name ?? '',
          plan_club_description: data.plan_club_description ?? '',
          flat_rate: Number(data.flat_rate) || 0,
          plan_type: data.plan_type ?? 0,
          type_club: data.type_club ?? 0,
          membership_fee: membershipFeeNum,
          is_control_membership_fee_payment_system: data.is_control_membership_fee_payment_system ?? false,
          additionalInfo: data.additionalInfo ?? state.quoteResponse?.additionalInfo,
          payment: data.payment,
          promotional_months: data.promotional_months ?? 0
        },
        quotes: [
          ...(data.quotes ?? []).map((q: any) => ({
            product_id: q.product_id != null ? String(q.product_id) : '',
            product_name: q.product_name ?? '',
            product_description: q.product_description ?? '',
            product_price: Number(q.product_price) || 0,
            checked: q.checked ?? false,
            required: q.required ?? false,
            cover: q.cover ?? []
          })),
          ...(data.upsell ?? []).map((u: any) => ({
            product_id: u.product_id != null ? String(u.product_id) : '',
            product_name: u.product_name ?? '',
            product_description: u.product_description ?? '',
            product_price: Number(u.product_price) || 0,
            checked: u.is_checked ?? false,
            required: false,
            cover: u.cover ?? []
          }))
        ],
        products: [
          ...(data.quotes ?? []).filter((q: any) => q.checked),
          ...(data.upsell ?? []).filter((u: any) => u.is_checked === true)
        ].map((p: any) => ({
          product_id: p.product_id != null ? String(p.product_id) : '',
          product_name: p.product_name ?? '',
          product_description: p.product_description ?? '',
          product_price: Number(p.product_price) || 0
        })),
        totalValue: totalNum,
        serviceCharge: financialChargesNum,
        customer: {
          name: data.customer?.name ?? '',
          email: data.customer?.email ?? '',
          mobile_phone: data.customer?.mobile_phone ?? '',
          document: data.customer?.document ?? '',
          document_type: data.customer?.document_type ?? '',
          birthdate: data.customer?.birthdate ?? ''
        }
      }));

      // Atualizar dados de promoção se disponíveis
      if (data.promotional_months) {
        useStore.setState((state) => ({
          promotionData: {
            ...state.promotionData,
            has_promotion: data.promotional_months > 0,
            is_promotion_available: data.promotional_months > 0
          }
        }));
      }

      // Detectar se é fluxo Aride Park (produto ID 8 ativo e nenhum outro ativo)
      if (isArideParkFlowFromAPI(data.quotes)) {
        console.log('🎯 Fluxo Aride Park detectado!');
        useStore.setState((state) => ({
          promotionData: {
            ...state.promotionData,
            has_promotion: true,
            is_promotion_available: true
          }
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar dados da cotação:', error);
      reportError('Erro ao buscar dados da cotação', error, {
        context: 'useQuoteData_fetchQuoteData',
        quoteIdParam: urlParams.quoteId,
        planParam: urlParams.plan,
        distribuitorParam: urlParams.distribuitor
      });

      storeState.addToast({
        title: 'Erro ao carregar cotação',
        message: 'Não foi possível carregar os dados da cotação. Verifique se o link está correto e tente novamente.',
        type: 'error'
      });
    } finally {
      setQuoteLoading(false);
    }
  }, [urlParams, skipFetch]); // Dependência memoizada

  // useEffect para executar fetch quando parâmetros mudarem
  useEffect(() => {
    console.log('🔄 useQuoteData useEffect executado', { quoteIdParam: urlParams.quoteId });
    if (skipFetch) {
      setQuoteLoading(false);
      return;
    }
    fetchQuoteData();
  }, [fetchQuoteData, skipFetch]); // Dependência da função memoizada

  // Memoizar função de navegação para redirecionamento
  const handleRedirectToHome = useCallback(() => {
    if (skipFetch) {
      return;
    }
    if (!quoteLoading && redirectToHome && quotes.length === 0 && !urlParams.quoteId) {
      const queryTail = `${urlParams.distribuitor ? `&dis=${urlParams.distribuitor}` : ''}${
        urlParams.org ? `&org=${urlParams.org}` : ''
      }${urlParams.seller ? `&seller=${urlParams.seller}` : ''}`;
      navigate(`/?modal=true&plan=${urlParams.plan}${queryTail}`);
    }
  }, [quotes, quoteLoading, redirectToHome, urlParams, navigate, skipFetch]);

  // useEffect para redirecionamento
  useEffect(() => {
    handleRedirectToHome();
  }, [handleRedirectToHome]);

  // Memoizar função de refetch para evitar re-criação desnecessária
  const refetchQuoteData = useCallback(() => {
    return fetchQuoteData();
  }, [fetchQuoteData]);

  return {
    quoteLoading,
    quoteData,
    refetchQuoteData
  };
};
